import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { LoadingComponent } from '@app/admin/manage/modals/loading-component/loading.component';
import { AccessLevelTypes } from '@app/enums/AccessLevelTypes';
import { CreateAccessRequestSSORequest } from '@app/generated-api';
import { AccessRequestService } from '@app/v2/services/access-request/access-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import {
    MdsButtonComponent,
    MdsCardActionsComponent,
    MdsCardComponent,
    MdsCardContentComponent,
    MdsDropdownComponent,
    MdsOption,
    MdsTextareaComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { AuthService } from '@services/auth/auth.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { filter, map, of, shareReplay, switchMap, take, tap } from 'rxjs';
import {
    DisplayErrorDialogComponent,
    DisplayErrorDialogData,
} from '../dialogs/display-error-dialog/display-error-dialog.component';
import { PageComponent } from '../page/page.component';
import { AccessRequestSSOFormGroup, availableAccessLevels } from './create-access-request.formgroup';

@Component({
    selector: 'medpace-create-access-request-sso',
    templateUrl: './create-access-request-sso.component.html',
    imports: [
        AlertComponent,
        MdsTextareaComponent,
        MdsButtonComponent,
        MdsCardComponent,
        MdsCardContentComponent,
        MdsCardActionsComponent,
        PageComponent,
        ReactiveFormsModule,
        LoadingComponent,
        MdsDropdownComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAccessRequestSSOComponent {
    private readonly userService2 = inject(UserServiceV2);
    private readonly matDialog = inject(MatDialog);
    private readonly authService = inject(AuthService);
    private readonly snackBarService = inject(SnackbarService);
    private readonly accessRequestService2 = inject(AccessRequestService);

    formGroup: AccessRequestSSOFormGroup = new AccessRequestSSOFormGroup();
    accessLevels: MdsOption[] = availableAccessLevels;

    isAssistant = toSignal(
        this.formGroup.controls.userAccessGroup.valueChangesStartWithCurrentValue().pipe(
            map((value) => value === AccessLevelTypes.Assistant),
            shareReplay(1)
        )
    );

    readonly isSuccess = signal(false);
    readonly errorMessageVisible = signal(false);
    readonly isLoading = signal(true);

    email: string = this.authService.claims['email'];

    readonly isAuth$ = this.authService.getIsAuthenticated().pipe(shareReplay(1));
    readonly isAuthSignal = toSignal(this.isAuth$);

    readonly userDataSignal = toSignal(
        this.isAuth$.pipe(
            take(1),
            filter(Boolean),
            switchMap(() => this.userService2.hasAccount.get$()),
            switchMap((hasAccount) => (hasAccount ? this.userService2.currentUserData.get$() : of(null))),
            tap(() => {
                this.isLoading.set(false);
            })
        )
    );

    readonly title = 'Access request';

    sendRequest() {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        this.accessRequestService2
            .createSSOAccessRequest(this.createObject())
            .pipe(take(1))
            .subscribe({
                error: (e) => {
                    console.error('error', e);
                    this.matDialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(
                        DisplayErrorDialogComponent,
                        {
                            ...getModalConfig(ModalSize.Small),
                            data: { description: e.error.ExceptionMessage },
                        }
                    );
                },
                complete: () => {
                    this.snackBarService.openInfoSnackbar('Request has been sent successfully.');
                    this.isSuccess.set(true);
                },
            });
    }

    createObject(): CreateAccessRequestSSORequest {
        return <CreateAccessRequestSSORequest>{
            userEmail: this.email,
            userAccessGroupId: this.formGroup.controls.userAccessGroup?.value,
            justification: this.formGroup.controls.justification?.value,
        };
    }
}
